<template>
  <div class="offer-form">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ isEditing ? $t("offers.edit") : $t("offers.add") }}</h2>
      <b-button variant="secondary" @click="goBack">
        <i class="fas fa-arrow-left mr-2"></i>{{ $t("common.back") }}
      </b-button>
    </div>

    <b-card>
      <b-form @submit.prevent="submitForm" ref="offerForm">
        <div class="row">
          <!-- Basic Information -->
          <div class="col-md-6">
            <h5 class="mb-3">{{ $t("offers.basic_information") }}</h5>

            <b-form-group :label="$t('offers.name')" label-for="name">
              <b-form-input
                id="name"
                v-model="form.name"
                :placeholder="$t('offers.name')"
                required
                :state="!errors.name"
              ></b-form-input>
              <b-form-invalid-feedback v-if="errors.name">
                {{ errors.name[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group :label="$t('offers.description')" label-for="description">
              <ckeditor
                :editor="editor"
                v-model="form.description"
                :config="editorConfig"
              ></ckeditor>
            </b-form-group>

            <b-form-group
              :label="$t('offers.terms_conditions')"
              label-for="terms_conditions"
            >
              <ckeditor
                :editor="editor"
                v-model="form.terms_conditions"
                :config="editorConfig"
              ></ckeditor>
            </b-form-group>

            <b-form-group :label="$t('offers.type')" label-for="type">
              <b-form-select
                id="type"
                v-model="form.type"
                :options="offerTypes"
                required
                :state="!errors.type"
              ></b-form-select>
              <b-form-invalid-feedback v-if="errors.type">
                {{ errors.type[0] }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="$t('offers.value')" label-for="value">
              <b-form-input
                id="value"
                v-model.number="form.value"
                type="number"
                step="0.01"
                min="0"
                :placeholder="$t('offers.value')"
                required
                :state="!errors.value"
              ></b-form-input>
              <b-form-invalid-feedback v-if="errors.value">
                {{ errors.value[0] }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="$t('offers.max_discount')" label-for="max_discount">
              <b-form-input
                id="max_discount"
                v-model.number="form.max_discount"
                type="number"
                step="0.01"
                min="0"
                :placeholder="$t('offers.max_discount')"
              ></b-form-input>
            </b-form-group>
          </div>

          <!-- Conditions -->
          <div class="col-md-6">
            <h5 class="mb-3">{{ $t("offers.conditions_restrictions") }}</h5>

            <b-form-group
              :label="$t('offers.min_order_amount')"
              label-for="min_order_amount"
            >
              <b-form-input
                id="min_order_amount"
                v-model.number="form.min_order_amount"
                type="number"
                step="0.01"
                min="0"
                :placeholder="$t('offers.min_order_amount')"
              ></b-form-input>
            </b-form-group>

            <div class="row">
              <div class="col-md-6">
                <b-form-group :label="$t('offers.min_nights')" label-for="min_nights">
                  <b-form-input
                    id="min_nights"
                    v-model.number="form.min_nights"
                    type="number"
                    min="1"
                    :placeholder="$t('offers.min_nights')"
                  ></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group :label="$t('offers.max_nights')" label-for="max_nights">
                  <b-form-input
                    id="max_nights"
                    v-model.number="form.max_nights"
                    type="number"
                    min="1"
                    :placeholder="$t('offers.max_nights')"
                  ></b-form-input>
                </b-form-group>
              </div>
            </div>

            <b-form-group :label="$t('offers.hotel_id')" label-for="hotel_id">
              <b-form-select
                id="hotel_id"
                v-model="form.hotel_id"
                :options="hotelOptions"
                :placeholder="$t('offers.hotel_id')"
              >
                <template #first>
                  <option value="">{{ $t("offers.all_hotels") }}</option>
                </template>
              </b-form-select>
            </b-form-group>

            <b-form-group :label="$t('offers.promo_code')" label-for="promo_code">
              <b-form-input
                id="promo_code"
                v-model="form.promo_code"
                :placeholder="$t('offers.promo_code')"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>

        <!-- Room Types -->
        <div class="row mt-4">
          <div class="col-12">
            <h5 class="mb-3">{{ $t("offers.room_type_ids") }}</h5>
            <b-form-group :label="$t('offers.room_type_ids')" label-for="room_type_ids">
              <b-form-checkbox-group
                id="room_type_ids"
                v-model="form.room_type_ids"
                :options="roomTypeOptions"
                stacked
              ></b-form-checkbox-group>
              <small class="text-muted">{{ $t("offers.leave_empty_room_types") }}</small>
            </b-form-group>
          </div>
        </div>

        <!-- Days of Week -->
        <div class="row mt-4">
          <div class="col-12">
            <h5 class="mb-3">{{ $t("offers.day_of_week") }}</h5>
            <b-form-group :label="$t('offers.day_of_week')" label-for="day_of_week">
              <b-form-checkbox-group
                id="day_of_week"
                v-model="form.day_of_week"
                :options="dayOptions"
                stacked
              ></b-form-checkbox-group>
              <small class="text-muted">{{ $t("offers.leave_empty_days") }}</small>
            </b-form-group>
          </div>
        </div>

        <!-- Date Range -->
        <div class="row mt-4">
            <h5 class="mb-3">{{ $t("offers.validity_period") }}</h5>

            <div class="col-md-6">
            <b-form-group :label="$t('offers.starts_at')" label-for="starts_at">
              <b-form-input
                id="starts_at"
                v-model="form.starts_at"
                type="datetime-local"
                :placeholder="$t('offers.starts_at')"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group :label="$t('offers.expires_at')" label-for="expires_at">
              <b-form-input
                id="expires_at"
                v-model="form.expires_at"
                type="datetime-local"
                :placeholder="$t('offers.expires_at')"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>

        <!-- Pre-Checkin Offers -->
        <div class="row mt-4">
          <div class="col-12">
            <h5 class="mb-3">
              {{ $t("offers.pre_checkin_settings") || "Pre-Checkin Settings" }}
            </h5>
            <div class="row">
              <div class="col-md-6">
                <b-form-group
                  :label="
                    $t('offers.apply_during_precheckin') || 'Apply During Pre-Checkin'
                  "
                  label-for="apply_during_precheckin"
                >
                  <b-form-checkbox
                    id="apply_during_precheckin"
                    v-model="form.apply_during_precheckin"
                    name="apply_during_precheckin"
                  >
                    {{
                      $t("offers.enable_pre_checkin_offers") ||
                      "Enable this offer for pre-checkin"
                    }}
                  </b-form-checkbox>
                  <small class="text-muted">{{
                    $t("offers.pre_checkin_help") ||
                    "When enabled, this offer will be available during the pre-checkin process"
                  }}</small>
                </b-form-group>
              </div>
              <div class="col-md-6" v-if="form.apply_during_precheckin">
                <b-form-group
                  :label="
                    $t('offers.precheckin_discount_type') || 'Pre-Checkin Discount Type'
                  "
                  label-for="precheckin_discount_type"
                >
                  <b-form-select
                    id="precheckin_discount_type"
                    v-model="form.precheckin_discount_type"
                    :options="precheckinDiscountTypes"
                  >
                  </b-form-select>
                  <small class="text-muted">{{
                    $t("offers.precheckin_discount_help") ||
                    "Type of discount to apply during pre-checkin"
                  }}</small>
                </b-form-group>
              </div>
            </div>
            <div class="row" v-if="form.apply_during_precheckin">
              <div class="col-md-6">
                <b-form-group
                  :label="
                    $t('offers.precheckin_min_hours_before') ||
                    'Minimum Hours Before Check-in'
                  "
                  label-for="precheckin_min_hours_before"
                >
                  <b-form-input
                    id="precheckin_min_hours_before"
                    v-model.number="form.precheckin_min_hours_before"
                    type="number"
                    min="1"
                    max="72"
                    :placeholder="$t('offers.precheckin_min_hours_before') || '24'"
                  ></b-form-input>
                  <small class="text-muted">{{
                    $t("offers.precheckin_min_hours_help") ||
                    "Minimum hours before check-in when this offer becomes available (1-72 hours)"
                  }}</small>
                </b-form-group>
              </div>
            </div>
          </div>
        </div>

        <!-- Usage Limits -->
        <div class="row mt-4">
          <div class="col-md-6">
            <h5 class="mb-3">{{ $t("offers.usage_limits") }}</h5>
            <b-form-group :label="$t('offers.usage_limit')" label-for="usage_limit">
              <b-form-input
                id="usage_limit"
                v-model.number="form.usage_limit"
                type="number"
                min="1"
                :placeholder="$t('offers.usage_limit')"
              ></b-form-input>
              <small class="text-muted">{{ $t("offers.leave_empty_unlimited") }}</small>
            </b-form-group>
          </div>
          <div class="col-6">
            <h5 class="mb-3">{{ $t("offers.status") }}</h5>
            <b-form-group :label="$t('offers.status')" label-for="is_active">
              <b-form-checkbox id="is_active" v-model="form.is_active" name="is_active">
                {{ $t("offers.active") }}
              </b-form-checkbox>
            </b-form-group>
          </div>

          <!-- <div class="col-md-6">
            <b-form-group :label="$t('offers.usage_limit_per_user')" label-for="usage_limit_per_user">
              <b-form-input id="usage_limit_per_user" v-model.number="form.usage_limit_per_user" type="number" min="1"
                :placeholder="$t('offers.usage_limit_per_user')"></b-form-input>
              <small class="text-muted">{{ $t('offers.leave_empty_per_user') }}</small>
            </b-form-group>
          </div> -->
        </div>

        <!-- Submit Buttons -->
        <div class="row mt-4">
          <div class="col-12">
            <div class="d-flex justify-content-end gap-2">
              <b-button variant="secondary" @click="goBack">
                {{ $t("common.cancel") }}
              </b-button>
              <b-button type="submit" variant="primary" :disabled="submitting">
                <span v-if="submitting">
                  <b-spinner small></b-spinner>
                  {{ $t("common.saving") }}
                </span>
                <span v-else>
                  {{ isEditing ? $t("common.save") : $t("common.create") }}
                </span>
              </b-button>
            </div>
          </div>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

export default {
  name: "OfferForm",
  data() {
    return {
      form: {
        name: "",
        description: "",
        terms_conditions: "",
        type: "",
        value: 0,
        max_discount: null,
        min_order_amount: null,
        min_nights: null,
        max_nights: null,
        starts_at: "",
        expires_at: "",
        usage_limit: null,
        // usage_limit_per_user: null,
        is_active: true,
        hotel_id: "",
        promo_code: "",
        room_type_ids: [],
        day_of_week: [],
        seasonal_dates: [],
        conditions: {},
        // Pre-checkin fields
        apply_during_precheckin: false,
        precheckin_discount_type: "percentage",
        precheckin_min_hours_before: 24,
      },
      errors: {},
      submitting: false,
      hotels: [],
      roomTypes: [],
      isEditing: false,
      offerId: null,
      editor: ClassicEditor,
      editorConfig: {
        toolbar: [
          "bold",
          "italic",
          "underline",
          "bulletedList",
          "numberedList",
          "|",
          "undo",
          "redo",
        ],
        placeholder: "Enter content here...",
        height: "200px",
        enterMode: "ENTER_BR",
        shiftEnterMode: "ENTER_P",
        autoParagraph: false,
        fillEmptyBlocks: false,
      },
    };
  },
  computed: {
    offerTypes() {
      return [
        { value: "percentage", text: this.$t("offers.types.percentage") },
        { value: "fixed", text: this.$t("offers.types.fixed") },
        { value: "free_night", text: this.$t("offers.types.free_night") },
      ];
    },
    hotelOptions() {
      return this.hotels.map((hotel) => ({
        value: hotel.id,
        text: hotel.name,
      }));
    },
    roomTypeOptions() {
      return this.roomTypes.map((roomType) => ({
        value: roomType.id,
        text: roomType.name,
      }));
    },
    dayOptions() {
      return [
        { value: 0, text: this.$t("offers.days.0") },
        { value: 1, text: this.$t("offers.days.1") },
        { value: 2, text: this.$t("offers.days.2") },
        { value: 3, text: this.$t("offers.days.3") },
        { value: 4, text: this.$t("offers.days.4") },
        { value: 5, text: this.$t("offers.days.5") },
        { value: 6, text: this.$t("offers.days.6") },
      ];
    },
    precheckinDiscountTypes() {
      return [
        { value: "percentage", text: this.$t("offers.types.percentage") || "Percentage" },
        { value: "fixed", text: this.$t("offers.types.fixed") || "Fixed Amount" },
        { value: "upgrade", text: this.$t("offers.types.upgrade") || "Room Upgrade" },
      ];
    },
  },
  async created() {
    await this.fetchHotels();
    await this.fetchRoomTypes();

    // Check if editing
    if (this.$route.params.id) {
      this.isEditing = true;
      this.offerId = this.$route.params.id;
      await this.fetchOffer();
    }
  },
  methods: {
    async fetchHotels() {
      try {
        const response = await request({
          method: "get",
          url: "/hotels",
        });
        this.hotels = response.data || [];
      } catch (error) {
        console.error("Error fetching hotels:", error);
      }
    },

    async fetchRoomTypes() {
      try {
        const response = await request({
          method: "get",
          url: "/room-types",
        });
        this.roomTypes = response.data || [];
      } catch (error) {
        console.error("Error fetching room types:", error);
      }
    },

    async fetchOffer() {
      try {
        const response = await request({
          method: "get",
          url: `/offers/${this.offerId}`,
        });

        const offer = response.data;
        this.form = {
          name: offer.name || "",
          description: offer.description || "",
          terms_conditions: offer.terms_conditions || "",
          type: offer.type || "",
          value: offer.value || 0,
          max_discount: offer.max_discount || null,
          min_order_amount: offer.min_order_amount || null,
          min_nights: offer.min_nights || null,
          max_nights: offer.max_nights || null,
          starts_at: offer.starts_at ? this.formatDateTimeLocal(offer.starts_at) : "",
          expires_at: offer.expires_at ? this.formatDateTimeLocal(offer.expires_at) : "",
          usage_limit: offer.usage_limit || null,
          // usage_limit_per_user: offer.usage_limit_per_user || null,
          is_active: offer.is_active !== undefined ? offer.is_active : true,
          hotel_id: offer.hotel_id || "",
          promo_code: offer.promo_code || "",
          room_type_ids: offer.room_type_ids || [],
          day_of_week: offer.day_of_week || [],
          seasonal_dates: offer.seasonal_dates || [],
          conditions: offer.conditions || {},
          // Pre-checkin fields
          apply_during_precheckin: offer.apply_during_precheckin || false,
          precheckin_discount_type: offer.precheckin_discount_type || "percentage",
          precheckin_min_hours_before: offer.precheckin_min_hours_before || 24,
        };
        console.log("Loaded offer:", this.form);
      } catch (error) {
        console.error("Error fetching offer:", error);
        this.$bvToast.toast("Error loading offer details", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    formatDateTimeLocal(dateString) {
      const date = new Date(dateString);
      return date.toISOString().slice(0, 16);
    },

    async submitForm() {
      this.submitting = true;
      this.errors = {};

      try {
        const formData = { ...this.form };

        // Convert datetime-local to ISO string
        if (formData.starts_at) {
          formData.starts_at = new Date(formData.starts_at).toISOString();
        }
        if (formData.expires_at) {
          formData.expires_at = new Date(formData.expires_at).toISOString();
        }
        // Clean up HTML content before submitting
        if (formData.description) {
          formData.description = this.cleanHtmlContent(formData.description);
        }
        if (formData.terms_conditions) {
          formData.terms_conditions = this.cleanHtmlContent(formData.terms_conditions);
        }

        console.log("Submitting form data:", formData);
        console.log("Pre-checkin fields:", {
          apply_during_precheckin: formData.apply_during_precheckin,
          precheckin_discount_type: formData.precheckin_discount_type,
          precheckin_min_hours_before: formData.precheckin_min_hours_before,
        });

        const url = this.isEditing ? `/offers/${this.offerId}` : "/offers";
        const method = this.isEditing ? "put" : "post";

        await request({
          method,
          url,
          data: formData,
        });

        this.$bvToast.toast(
          this.isEditing
            ? this.$t("offers.updated_successfully")
            : this.$t("offers.created_successfully"),
          {
            title: this.$t("common.success"),
            variant: "success",
            solid: true,
          }
        );

        this.$router.push("/admin/offers");
      } catch (error) {
        console.error("Error submitting form:", error);

        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$bvToast.toast(error.response?.data?.message || "An error occurred", {
            title: this.$t("common.error"),
            variant: "danger",
            solid: true,
          });
        }
      } finally {
        this.submitting = false;
      }
    },

    goBack() {
      this.$router.push("/admin/offers");
    },

    cleanHtmlContent(html) {
      if (!html) return "";

      // Remove empty paragraphs with &nbsp;
      html = html.replace(/<p>\s*&nbsp;\s*<\/p>/g, "");

      // Remove empty paragraphs
      html = html.replace(/<p>\s*<\/p>/g, "");

      // Remove extra whitespace
      html = html.replace(/\s+/g, " ");

      // Remove leading/trailing whitespace
      html = html.trim();

      // If content is empty after cleaning, return empty string
      if (html === "" || html === "<p></p>" || html === "<p>&nbsp;</p>") {
        return "";
      }

      return html;
    },
  },
};
</script>

<style scoped>
.offer-form {
  padding: 20px;
}

.card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 1rem;
}

.btn {
  border-radius: 6px;
}

/* Improved checkbox styling */
.custom-checkbox {
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.custom-checkbox:hover {
  background-color: #f8f9fa;
}

.custom-checkbox .custom-control-label {
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  padding-left: 0.5rem;
}

.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

.custom-checkbox .custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Compact form spacing */
.form-group label {
  margin-bottom: 0.25rem;
  font-weight: 600;
  font-size: 0.9rem;
}

.form-control,
.form-select {
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}

/* Smaller section headers */
h5 {
  font-size: 1.1rem;
  margin-bottom: 1rem;
}

/* Compact row spacing */
.row {
  margin-bottom: 0.5rem;
}

.row.mt-4 {
  margin-top: 1.5rem !important;
}

/* Better checkbox group layout */
.b-form-checkbox-group {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background-color: #f8f9fa;
}

.b-form-checkbox-group::-webkit-scrollbar {
  width: 6px;
}

.b-form-checkbox-group::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.b-form-checkbox-group::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.b-form-checkbox-group::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Simple Text Editor Styles */
.simple-editor {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.editor-toolbar {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 8px;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
}

.toolbar-btn {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  color: #495057;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.toolbar-btn:hover {
  background-color: #e9ecef;
  border-color: #007bff;
  color: #007bff;
}

.toolbar-btn:active {
  background-color: #007bff;
  color: #fff;
  border-color: #007bff;
}

.editor-textarea {
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: none;
  outline: none;
  font-size: 14px;
  line-height: 1.6;
  color: #495057;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  resize: vertical;
}

.editor-textarea:focus {
  outline: none;
  box-shadow: inset 0 0 0 1px #007bff;
}
</style>
